// Related articles, small 4-card grid surfaced at the foot of every blog and
// KB article. Ranks candidates by tag overlap (with a same-category bonus) and
// gracefully degrades to "most recent" when no overlap exists.
//
// The component is content-agnostic, pass it any list of CMS items and tell
// it which fields to use for the title, summary, tags and link path.
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { trackEvent, GA_EVENTS } from "@/lib/analytics";

function asText(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value.map(asText).filter(Boolean).join(", ");
  }
  if (typeof value === "object") {
    return value.name || value.title || value.slug || value.label || "";
  }
  return String(value);
}

function asTags(tags) {
  return Array.isArray(tags) ? tags.map(asText).filter(Boolean) : [];
}

function asCategory(category) {
  return asText(category);
}

function scoreCandidate(cand, currentTags, currentCategory) {
  const tags = new Set(asTags(cand.tags).map((t) => t.toLowerCase()));
  const category = asCategory(cand.category).toLowerCase();
  const currentCategoryText = asCategory(currentCategory).toLowerCase();
  let score = 0;
  for (const t of currentTags || []) {
    if (tags.has((t || "").toLowerCase())) score += 2;
  }
  if (currentCategoryText && category && category === currentCategoryText) {
    score += 1;
  }
  return score;
}

/**
 * @param items          [{ slug, title|headline, excerpt|summary, tags, category, cover, readMins }]
 * @param current        { slug, tags, category }
 * @param basePath       "/blog" | "/kb"
 * @param title          Section heading
 * @param emptyMessage   Heading when no related articles exist
 * @param max            How many cards to show (default 4)
 * @param testId         data-testid for the section
 */
export default function RelatedArticles({
  items = [],
  current = {},
  basePath = "/blog",
  title = "Related reading",
  emptyMessage = null,
  max = 4,
  testId = "related-articles",
}) {
  const ranked = useMemo(() => {
    const currentTags = asTags(current.tags);
    const currentCategory = asCategory(current.category);
    const pool = (items || []).filter((i) => i.slug && i.slug !== current.slug);
    const scored = pool.map((c) => ({ ...c, _score: scoreCandidate(c, currentTags, currentCategory) }));
    scored.sort((a, b) => b._score - a._score);
    // If no overlap at all, the sort still works, items keep their incoming
    // order (usually most-recent first).
    return scored.slice(0, max);
  }, [items, current.slug, current.tags, current.category, max]);

  if (!ranked.length) return null;

  return (
    <section className="py-14 md:py-20 bg-secondary/40 border-t border-border" data-testid={testId}>
      <div className="container-cv">
        <div className="flex items-end justify-between gap-4 mb-8">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">{title}</h2>
          <Link
            to={basePath}
            className="hidden sm:inline-flex items-center gap-1 text-sm font-bold text-primary hover:text-accent transition-colors"
            data-testid={`${testId}-see-all`}
          >
            All articles <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {emptyMessage && !ranked.length && (
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {ranked.map((p, idx) => (
            <Link
              key={p.slug}
              to={`${basePath}/${p.slug}`}
              data-testid={`${testId}-card-${p.slug}`}
              onClick={() =>
                trackEvent(GA_EVENTS.RELATED_ARTICLE_CLICK, {
                  source_slug: current.slug || "",
                  target_slug: p.slug,
                  base_path: basePath,
                  position: idx + 1,
                })
              }
              className="group rounded-2xl border border-border bg-white overflow-hidden hover:-translate-y-1 hover:shadow-md hover:border-accent/60 transition-all flex flex-col"
            >
              {p.cover ? (
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={p.cover}
                    alt={p.title || p.headline}
                    loading="lazy"
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ) : null}
              <div className="p-5 flex flex-col flex-1">
                {asCategory(p.category) && (
                  <div className="text-[11px] font-bold uppercase tracking-wider text-accent">{asCategory(p.category)}</div>
                )}
                <h3 className="mt-1.5 font-display text-base font-bold text-primary leading-snug group-hover:text-accent transition-colors">
                  {asText(p.title) || asText(p.headline)}
                </h3>
                {(asText(p.excerpt) || asText(p.summary)) && (
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                    {asText(p.excerpt) || asText(p.summary)}
                  </p>
                )}
                <div className="mt-auto pt-4 flex items-center gap-1 text-xs font-bold text-primary group-hover:text-accent">
                  Read article <ArrowUpRight className="h-3.5 w-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
